import type { AttemptFeedback, ProblemModel, PublicProblem, WorkedAnswerDraft } from "../shared/domain";

export interface PracticeView {
  id: string;
  date: string;
  sequence: number;
  status: "in_progress" | "completed";
  badgeId: string | null;
  completedAt: string | null;
  problems: PublicProblem[];
}

export interface HistoryEntry {
  date: string;
  status: string;
  badgeId: string | null;
  completedAt: string | null;
  solved: number;
  assisted: number;
}

export interface DashboardView {
  today: { status: string; solved: number; total: number };
  recent: { independent: number; assisted: number };
  models: Array<{ id: ProblemModel; label: string; successes: number; mastered: boolean }>;
  errors: { relationship: number; operation: number; calculation: number; unit: number };
  nextFocus: string;
  recentProblems: Array<{ id: string; prompt: string; model: string; status: string; attempts: number; date: string }>;
  settings: { pauseNewModels: boolean; difficultyShift: -1 | 0 | 1 };
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    headers: init?.body ? { "Content-Type": "application/json", ...init.headers } : init?.headers,
    ...init,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText })) as { error?: string };
    throw new ApiError(body.error ?? response.statusText, response.status);
  }
  return response.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

export const client = {
  today: () => api<PracticeView>("/api/practice/today"),
  history: () => api<HistoryEntry[]>("/api/history"),
  saveDraft: (id: string, draft: WorkedAnswerDraft) => api<{ ok: true }>(`/api/problems/${id}/draft`, { method: "POST", body: JSON.stringify(draft) }),
  defer: (id: string) => api<{ ok: true }>(`/api/problems/${id}/defer`, { method: "POST", body: "{}" }),
  attempt: (id: string, draft: WorkedAnswerDraft) => api<AttemptFeedback>(`/api/problems/${id}/attempt`, { method: "POST", body: JSON.stringify(draft) }),
  parentLogin: (password: string) => api<{ ok: true }>("/api/parent/login", { method: "POST", body: JSON.stringify({ password }) }),
  parentLogout: () => api<{ ok: true }>("/api/parent/logout", { method: "POST", body: "{}" }),
  dashboard: () => api<DashboardView>("/api/parent/dashboard"),
  updateSettings: (settings: DashboardView["settings"]) => api<{ ok: true }>("/api/parent/settings", { method: "PUT", body: JSON.stringify(settings) }),
  flag: (id: string, reason: string) => api<{ ok: true }>(`/api/parent/problems/${id}/flag`, { method: "POST", body: JSON.stringify({ reason }) }),
  clearData: (password: string) => api<{ ok: true }>("/api/parent/data", { method: "DELETE", body: JSON.stringify({ password }) }),
};

